// Ring App Store account-link user-facing strings (LLD 09 Appendix A).
//
// This is the cert-readiness contract. The backend speaks only stable error
// CODES (RingLinkErrorCode + the /oidc/ring-link-callback error codes); all
// user-facing copy lives HERE so the SPA can render it on the pre-session
// install pages. The exact literals are pinned by ringLinkMessages.test.ts —
// any drift is a cert regression (do NOT paraphrase).
//
// Migrated verbatim from the former server-rendered RingAppStoreHttpHandler
// constants + AccountLinkForm, reconciling the prior drift (e.g. the inline
// "Invalid link URL." / "Link expired." variants that had diverged from the
// canonical em-dash phrasing).

/** Error codes the backend emits on the /link-error route and the submit API. */
export type RingLinkErrorCode =
  | "invalid_link_url"
  | "invalid_link_state"
  | "link_expired"
  | "session_expired"
  | "name_invalid"
  | "tenant_already_bound"
  | "tenant_bound_to_different_alias"
  | "auth_failed"
  | "internal";

/**
 * Canonical user-facing message for each error code. The
 * `tenant_bound_to_different_alias` entry is a function because LLD 09 §4.3
 * row 1 requires the existing admin alias substituted inline.
 */
export const RING_LINK_MESSAGES: Record<
  Exclude<RingLinkErrorCode, "tenant_bound_to_different_alias">,
  string
> = {
  // LLD 09 Appendix A row 4.
  invalid_link_url: "Invalid link URL — please reinstall from the Ring app.",
  // No valid LinkState for the supplied id — same remediation as an expired link.
  invalid_link_state: "Invalid link URL — please reinstall from the Ring app.",
  // LLD 09 Appendix A rows 5 & 7.
  link_expired: "Link expired — please reinstall from the Ring app.",
  // Pre-session OIDC state expired between authorize and callback.
  session_expired: "Link expired — please reinstall from the Ring app.",
  // Workspace-name validation (3–80 chars).
  name_invalid: "Workspace name must be between 3 and 80 characters.",
  // LLD 09 §4.3: this Ring account already maps to a workspace owned by the caller.
  tenant_already_bound:
    "This Ring account is already linked to an existing workspace.",
  // Federate auth failure during the install callback.
  auth_failed: "Authentication failed. Please reinstall from the Ring app.",
  // Catch-all server error.
  internal: "We can't finish linking right now. Please reinstall the bridge from the Ring app and try again.",
};

/** LLD 09 §4.3 failure-table row 1 — alias substituted from the GSI lookup. */
export function tenantBoundToDifferentAliasMessage(alias: string | null | undefined): string {
  const who = alias && alias.trim() ? alias.trim() : "another administrator";
  return `This Ring account is already linked to a tenant administered by ${who}.`;
}

/** Resolves any error code (with optional alias context) to its message. */
export function ringLinkMessage(code: string | null | undefined, alias?: string | null): string {
  if (code === "tenant_bound_to_different_alias") {
    return tenantBoundToDifferentAliasMessage(alias);
  }
  const known = code != null && code in RING_LINK_MESSAGES
    ? RING_LINK_MESSAGES[code as keyof typeof RING_LINK_MESSAGES]
    : undefined;
  return known ?? RING_LINK_MESSAGES.internal;
}
